/**
 * FoodPulseCityDirectory — "Every food business in <City>" landing.
 *
 * Route: /food-pulse/city/:country/:state/:city
 * Backend: GET /api/city-directory/:country/:state/:city
 *
 * Renders a searchable, deduplicated list of every food business in
 * the target city, grouped by category. Each card exposes a
 * "Claim this business" CTA that jumps to the business detail page
 * with ?claim=1 so the existing OTP-based claim modal auto-opens.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, MapPin, Phone, Globe, BadgeCheck, ArrowLeft, Loader2,
  Store, Utensils, Coffee, Wheat, ShoppingCart, Truck, ChefHat,
  IceCream, HeartHandshake, ThumbsUp,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import CityContentPanel from "@/pages/city-directory/CityContentPanel";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import TalkVibesTab from "@/components/TalkVibesTab";
import FoodNewsFeed from "@/components/FoodNewsFeed";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  restaurant:      { label: "Restaurants",       icon: Utensils      },
  fast_food:       { label: "Fast food",         icon: Utensils      },
  cafe:            { label: "Cafes",             icon: Coffee        },
  coffee_shop:     { label: "Coffee shops",      icon: Coffee        },
  bakery:          { label: "Bakeries",          icon: Wheat         },
  pastry:          { label: "Pastry",            icon: Wheat         },
  supermarket:     { label: "Supermarkets",      icon: ShoppingCart  },
  grocery:         { label: "Groceries",         icon: ShoppingCart  },
  convenience_store: { label: "Convenience",     icon: ShoppingCart  },
  health_food:     { label: "Health food",       icon: ShoppingCart  },
  food_truck:      { label: "Food trucks",       icon: Truck         },
  food_cart:       { label: "Food carts",        icon: Truck         },
  farmers_market:  { label: "Farmers markets",   icon: Wheat         },
  farm:            { label: "Farms",             icon: Wheat         },
  seafood_shop:    { label: "Seafood",           icon: Store         },
  butcher:         { label: "Butchers",          icon: Store         },
  bar:             { label: "Bars",              icon: Store         },
  brewery:         { label: "Breweries",         icon: Store         },
  ice_cream:       { label: "Ice cream",         icon: IceCream      },
  chocolate_shop:  { label: "Chocolate",         icon: IceCream      },
  food_bank:       { label: "Food banks",        icon: HeartHandshake},
  caterer:         { label: "Caterers",          icon: ChefHat       },
  food_distributor:{ label: "Distributors",      icon: Truck         },
};

function prettyCity(slug) {
  if (!slug) return "";
  return decodeURIComponent(slug).replace(/-/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function BusinessCard({ b }) {
  const Icon = (TYPE_META[b.business_type] || { icon: Store }).icon;
  const detail = `/food-pulse/businesses/${b.slug}`;
  const claim  = `${detail}?claim=1`;
  const claimed = b.claim_status === "claimed";
  const [vouches, setVouches] = useState(b.vouches_count || 0);
  const [vouching, setVouching] = useState(false);
  const vouch = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setVouching(true);
    try {
      const r = await http.post(`/food-businesses/${b.slug}/vouch`, {});
      setVouches(r.data.vouches);
      if (r.data.already_verified) toast.info("This listing is already verified.");
      else if (r.data.already_vouched) toast.info("You've already vouched.");
      else if (r.data.promoted) toast.success("3 vouches reached — admin will review!");
      else toast.success(`Vouched · ${r.data.vouches}/${r.data.threshold} needed`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401) toast.error("Please sign in to vouch for a business.");
      else toast.error(typeof detail === "string" ? detail : "Couldn't vouch — retry?");
    } finally {
      setVouching(false);
    }
  };
  return (
    <div
      className="rounded-xl border p-4 flex flex-col gap-3 bg-white hover:shadow-md transition-shadow"
      style={{ borderColor: "#EADFCB" }}
      data-testid="city-business-card"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "#FBEED6", color: "#8B5E10" }}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <Link
            to={detail}
            className="block font-semibold leading-tight hover:underline"
            style={{ color: "#3C2A17" }}
            data-testid="city-business-name"
          >
            {b.name}
          </Link>
          <div className="text-xs uppercase tracking-wide mt-0.5"
               style={{ color: "#8B5E10" }}>
            {(TYPE_META[b.business_type]?.label) || (b.business_type || "Food business").replace(/_/g, " ")}
            {claimed && (
              <span className="inline-flex items-center gap-1 ml-2 text-emerald-700 normal-case">
                <BadgeCheck size={12}/> Verified owner
              </span>
            )}
          </div>
        </div>
      </div>

      {b.address && (
        <div className="text-xs flex items-start gap-1.5" style={{ color: "#5A4A32" }}>
          <MapPin size={12} className="mt-0.5 shrink-0" />
          <span className="truncate">{b.address}{b.zip ? ` · ${b.zip}` : ""}</span>
        </div>
      )}
      {b.phone && (
        <div className="text-xs flex items-center gap-1.5" style={{ color: "#5A4A32" }}>
          <Phone size={12}/> <span>{b.phone}</span>
        </div>
      )}
      {b.website && (
        <div className="text-xs flex items-center gap-1.5 truncate" style={{ color: "#5A4A32" }}>
          <Globe size={12}/>
          <a href={b.website} target="_blank" rel="noreferrer"
             className="truncate hover:underline"
             onClick={(e) => e.stopPropagation()}>
            {b.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </a>
        </div>
      )}

      <div className="flex gap-2 mt-1">
        <Link
          to={detail}
          className="flex-1 text-center text-xs font-medium py-2 rounded-lg border"
          style={{ borderColor: "#EADFCB", color: "#3C2A17" }}
          data-testid="city-business-view"
        >
          View profile
        </Link>
        {!claimed && (
          <Link
            to={claim}
            className="flex-1 text-center text-xs font-semibold py-2 rounded-lg text-white"
            style={{ background: "#B25D38" }}
            data-testid="city-business-claim"
          >
            Claim it
          </Link>
        )}
      </div>
      {!claimed && (
        <button
          onClick={vouch}
          disabled={vouching}
          className="w-full text-[11px] py-1.5 rounded-lg border inline-flex items-center justify-center gap-1 disabled:opacity-50"
          style={{ borderColor: "#EADFCB", color: "#5A4A32", background: "#FDF6E3" }}
          data-testid="city-business-vouch"
          title="Vouch this business is real and open — 3 vouches trigger admin review"
        >
          <ThumbsUp className="w-3 h-3"/>
          {vouching ? "…" : `Vouch it's real${vouches > 0 ? ` · ${vouches}/3` : ""}`}
        </button>
      )}
    </div>
  );
}

export default function FoodPulseCityDirectory() {
  const { country = "US", state = "", city = "" } = useParams();
  const cityLabel  = prettyCity(city);
  const stateLabel = (state || "").toUpperCase();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [q, setQ]             = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const url = `${API}/api/city-directory/${country.toUpperCase()}/${state.toUpperCase()}/${encodeURIComponent(cityLabel)}?limit=2000`;
        const res = await axios.get(url);
        if (cancelled) return;
        setData(res.data);
        document.title = `Food businesses in ${cityLabel}, ${stateLabel} — Food Pulse`;
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [country, state, city, cityLabel, stateLabel]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    const needle = q.trim().toLowerCase();
    return data.items.filter((b) => {
      if (activeCat !== "all" && b.business_type !== activeCat) return false;
      if (!needle) return true;
      const hay = `${b.name || ""} ${b.address || ""} ${(b.cuisines || []).join(" ")}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [data, q, activeCat]);

  const groupedByType = useMemo(() => {
    const out = {};
    for (const b of filtered) {
      const k = b.business_type || "other";
      (out[k] = out[k] || []).push(b);
    }
    return out;
  }, [filtered]);

  const catOrder = useMemo(() => {
    const cats = Object.keys(data?.by_type || {});
    return cats.sort((a, b) => (data.by_type[b].length - data.by_type[a].length));
  }, [data]);

  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7" }}>
      <SeoHead
        title={`Food businesses in ${cityLabel}, ${stateLabel} — Food Pulse`}
        description={`Every restaurant, cafe, bakery, market and food truck in ${cityLabel}, ${stateLabel}. Verified owners can claim & enhance their listing on Food Pulse.`}
      />

      <div className="max-w-6xl mx-auto px-4 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <Link to="/food-pulse/city"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider"
                style={{ color: "#8B5E10" }}
                data-testid="city-back-to-hub">
            <ArrowLeft size={14}/> All US cities
          </Link>
          <Link to="/food-pulse" className="hidden sm:flex items-center gap-2" data-testid="city-fp-logo">
            <img
              src="/foodpulse/fp-logo-round.jpg"
              alt="Food Pulse"
              className="w-10 h-10 rounded-full ring-2 ring-white shadow"
            />
            <span className="hidden sm:block">
              <span className="block text-sm font-bold leading-none" style={{ color: "#3C2A17" }}>
                Food Pulse<sup className="text-[9px]"></sup>
              </span>
              <span className="block text-[9px] italic" style={{ color: "#8B5E10" }}>
                Social Food Discovery App
              </span>
            </span>
          </Link>
        </div>
        <h1 className="mt-3 text-2xl sm:text-4xl lg:text-5xl font-serif" style={{ color: "#3C2A17" }}>
          Food businesses in {cityLabel}
          <span className="ml-2 text-lg sm:text-xl font-normal" style={{ color: "#8B5E10" }}>
            {stateLabel}
          </span>
        </h1>
        {data?.totals && (
          <p className="mt-2 text-sm" style={{ color: "#5A4A32" }}>
            {data.totals.shown.toLocaleString()} unique listings ·{" "}
            {data.totals.dupes_merged > 0 && (
              <span>{data.totals.dupes_merged.toLocaleString()} duplicates merged · </span>
            )}
            {data.categories?.length || 0} categories · sourced from public directories & community contributions.
          </p>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 sticky top-0 z-10 pb-3"
           style={{ background: "linear-gradient(#FDFBF7,#FDFBF7 80%,rgba(253,251,247,0))" }}>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#8B5E10" }}/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${cityLabel} — e.g. taco, pizza, bakery, coffee…`}
            className="w-full pl-9 pr-4 py-3 rounded-xl border outline-none focus:ring-2"
            style={{ borderColor: "#EADFCB", background: "#fff" }}
            data-testid="city-search-input"
          />
        </div>

        {catOrder.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveCat("all")}
              className={`text-xs px-3 py-1.5 rounded-full border ${activeCat === "all" ? "text-white" : ""}`}
              style={activeCat === "all"
                ? { background: "#B25D38", borderColor: "#B25D38" }
                : { borderColor: "#EADFCB", color: "#5A4A32", background: "#fff" }}
              data-testid="city-cat-all"
            >
              All ({data?.totals?.shown || 0})
            </button>
            {catOrder.map((cat) => {
              const meta = TYPE_META[cat] || { label: cat.replace(/_/g, " ") };
              const count = data.by_type[cat].length;
              const on = activeCat === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCat(cat)}
                  className={`text-xs px-3 py-1.5 rounded-full border capitalize ${on ? "text-white" : ""}`}
                  style={on
                    ? { background: "#B25D38", borderColor: "#B25D38" }
                    : { borderColor: "#EADFCB", color: "#5A4A32", background: "#fff" }}
                  data-testid={`city-cat-${cat}`}
                >
                  {meta.label} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-16">
        <CityContentPanel country={country.toUpperCase()}
                          state={state.toUpperCase()}
                          city={cityLabel} />

        {loading && (
          <div className="py-16 flex items-center justify-center" style={{ color: "#8B5E10" }}>
            <Loader2 className="animate-spin mr-2" size={18}/> Loading {cityLabel} businesses…
          </div>
        )}
        {error && !loading && (
          <div className="py-10 text-center text-sm" style={{ color: "#B25D38" }}>
            Could not load businesses: {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="py-16 text-center text-sm" style={{ color: "#5A4A32" }}>
            No matches yet — try a different search or category.
          </div>
        )}
        {!loading && filtered.length > 0 && activeCat !== "all" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {filtered.map((b) => <BusinessCard key={b.id} b={b}/>)}
          </div>
        )}
        {!loading && filtered.length > 0 && activeCat === "all" && (
          <div className="space-y-10 mt-4">
            {catOrder
              .filter((cat) => groupedByType[cat]?.length)
              .map((cat) => {
                const items = groupedByType[cat];
                const meta  = TYPE_META[cat] || { label: cat.replace(/_/g, " ") };
                return (
                  <section key={cat} data-testid={`city-section-${cat}`}>
                    <div className="flex items-baseline justify-between mb-3">
                      <h2 className="text-lg font-semibold capitalize" style={{ color: "#3C2A17" }}>
                        {meta.label}
                        <span className="text-xs font-normal ml-2" style={{ color: "#8B5E10" }}>
                          {items.length}
                        </span>
                      </h2>
                      <button
                        onClick={() => setActiveCat(cat)}
                        className="text-xs underline"
                        style={{ color: "#8B5E10" }}>
                        See all
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.slice(0, 9).map((b) => <BusinessCard key={b.id} b={b}/>)}
                    </div>
                  </section>
                );
              })}
          </div>
        )}

        <div className="mt-14 border rounded-xl p-6 text-center"
             style={{ borderColor: "#EADFCB", background: "#FBEED6" }}>
          <h3 className="text-lg font-serif" style={{ color: "#3C2A17" }}>
            Own a food business in {cityLabel}?
          </h3>
          <p className="text-sm mt-1" style={{ color: "#5A4A32" }}>
            Claim your listing in under 60 seconds — free forever.
            Add your menu, hours, delivery links and photos.
          </p>
          <p className="text-xs mt-3" style={{ color: "#8B5E10" }}>
            Not listed yet? Ping{" "}
            <a href="mailto:hello@foodpulse.net" className="underline">hello@foodpulse.net</a>{" "}
            and we'll add you within 24 hours.
          </p>
        </div>

        <FoodNewsFeed
          country="US"
          state={state.toUpperCase()}
          city={cityLabel}
          className="mt-10"
        />

        <TalkVibesTab
          entityType="city"
          entityId={`US:${state.toUpperCase()}:${cityLabel}`}
          subject={`${cityLabel}, ${state.toUpperCase()}`}
        />
      </div>
    </div>
  );
}
